import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Boxes, RefreshCw, Plus, X, ExternalLink, Trash2, Edit2, Filter, Cloud,
  Database, Server, Monitor, Zap, Cpu, Briefcase, Layers, Shield, Link2,
  ChevronRight, Search, Users, ShieldAlert, AlertTriangle,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

// ── Types ────────────────────────────────────────────────────────────────────

interface StackItem { type: string; name: string; version: string }

interface Service {
  id: number
  name: string
  description: string
  tier: 1 | 2 | 3
  kind: string
  owner: string
  team: string
  cloud: string
  region: string
  stack: StackItem[]
  dependencies: string[]
  sla: string
  status: string
  url: string | null
  repo_url: string | null
  runbook_url: string | null
  tags: string[]
  notes: string
  last_reviewed_at: string | null
  created_at: string | null
  updated_at: string | null
}

interface Stats {
  total: number
  by_tier: Record<string, number>
  by_cloud: Record<string, number>
  by_kind: Record<string, number>
  by_status: Record<string, number>
  top_owners: { owner: string; count: number }[]
  stack: Record<string, { name: string; count: number }[]>
}

interface ServiceRisks {
  service_id: number
  service_name: string
  tier: number
  count: number
  top_priority: number
  risks: { id: string | number; title: string; priority: number; source: string }[]
}

type HealthBand = 'healthy' | 'warning' | 'concern' | 'critical'

interface ServiceHealth {
  id: number
  name: string
  tier: number
  kind: string
  owner: string
  status: string
  score: number
  band: HealthBand
  breakdown: { kind: string; delta: number; reason: string }[]
}

interface HealthSummary {
  count: number
  average: number
  min: number
  max: number
  by_band: Record<HealthBand, number>
  services: ServiceHealth[]
  worst: ServiceHealth[]
}

const HEALTH_BAND_BG: Record<HealthBand, string> = {
  healthy:  'bg-emerald/10 border-emerald/30 text-emerald',
  warning:  'bg-amber/10 border-amber/30 text-amber-light',
  concern:  'bg-orange/10 border-orange/30 text-orange',
  critical: 'bg-rose/10 border-rose/30 text-rose',
}

const HEALTH_BAND_TEXT: Record<HealthBand, string> = {
  healthy:  'text-emerald',
  warning:  'text-amber-light',
  concern:  'text-orange',
  critical: 'text-rose',
}

// ── Visuals ──────────────────────────────────────────────────────────────────

const KIND_ICON: Record<string, React.ElementType> = {
  app: Boxes, api: Zap, db: Database, infra: Server, data: Layers,
  ml: Cpu, frontend: Monitor, worker: Cpu, scheduled: Monitor,
}

const TIER_META: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Tier 1 — Critical',    color: 'text-rose',    bg: 'bg-rose/10 border-rose/30' },
  2: { label: 'Tier 2 — Important',   color: 'text-amber-light', bg: 'bg-amber/10 border-amber/30' },
  3: { label: 'Tier 3 — Supporting',  color: 'text-emerald',    bg: 'bg-emerald/10 border-emerald/30' },
}

const STATUS_COLOR: Record<string, string> = {
  active: 'text-emerald',
  deprecating: 'text-amber-light',
  retired: 'text-text-muted',
  planned: 'text-blue-light',
}

const CLOUD_COLOR: Record<string, string> = {
  aws:     '#ff9900',
  azure:   '#0078d4',
  gcp:     '#4285f4',
  hetzner: '#d50c2d',
  'on-prem':'#71717a',
}

// ── Service form ─────────────────────────────────────────────────────────────

function ServiceForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Partial<Service>
  onSave: (s: any) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name || '')
  const [description, setDescription] = useState(initial?.description || '')
  const [tier, setTier] = useState<1 | 2 | 3>((initial?.tier as 1 | 2 | 3) || 2)
  const [kind, setKind] = useState(initial?.kind || 'app')
  const [owner, setOwner] = useState(initial?.owner || '')
  const [team, setTeam] = useState(initial?.team || '')
  const [cloud, setCloud] = useState(initial?.cloud || '')
  const [region, setRegion] = useState(initial?.region || '')
  const [sla, setSla] = useState(initial?.sla || '')
  const [status, setStatus] = useState(initial?.status || 'active')
  const [url, setUrl] = useState(initial?.url || '')
  const [repoUrl, setRepoUrl] = useState(initial?.repo_url || '')
  const [runbookUrl, setRunbookUrl] = useState(initial?.runbook_url || '')
  const [tags, setTags] = useState((initial?.tags || []).join(', '))
  const [notes, setNotes] = useState(initial?.notes || '')
  const [stack, setStack] = useState<StackItem[]>(initial?.stack || [])
  const [dependencies, setDependencies] = useState((initial?.dependencies || []).join(', '))

  const addStackItem = () => setStack([...stack, { type: 'runtime', name: '', version: '' }])
  const updateStackItem = (i: number, field: keyof StackItem, value: string) => {
    const next = [...stack]
    next[i] = { ...next[i], [field]: value }
    setStack(next)
  }
  const removeStackItem = (i: number) => setStack(stack.filter((_, j) => j !== i))

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="e.g. billing-api" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" placeholder="One line on what it does" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Tier</label>
          <div className="flex gap-1">
            {[1, 2, 3].map(n => (
              <button type="button" key={n} onClick={() => setTier(n as 1 | 2 | 3)}
                className={clsx('flex-1 py-1 text-[11px] rounded border transition-colors',
                  tier === n
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>
                Tier {n}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Kind</label>
          <select value={kind} onChange={e => setKind(e.target.value)} title="Kind" className="input py-1.5 text-[12px] w-full">
            <option value="app">App</option>
            <option value="api">API</option>
            <option value="frontend">Frontend</option>
            <option value="worker">Worker</option>
            <option value="scheduled">Scheduled</option>
            <option value="db">Database</option>
            <option value="data">Data pipeline</option>
            <option value="ml">ML / AI</option>
            <option value="infra">Infrastructure</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Owner</label>
          <input value={owner} onChange={e => setOwner(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="name@company.com" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Team</label>
          <input value={team} onChange={e => setTeam(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="Platform" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Cloud</label>
          <select value={cloud} onChange={e => setCloud(e.target.value)} title="Cloud" className="input py-1.5 text-[12px] w-full">
            <option value="">—</option>
            <option value="aws">AWS</option>
            <option value="azure">Azure</option>
            <option value="gcp">GCP</option>
            <option value="hetzner">Hetzner</option>
            <option value="digitalocean">DigitalOcean</option>
            <option value="cloudflare">Cloudflare</option>
            <option value="on-prem">On-prem</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Region</label>
          <input value={region} onChange={e => setRegion(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="eu-west-1" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">SLA</label>
          <input value={sla} onChange={e => setSla(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="99.9%" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)} title="Status" className="input py-1.5 text-[12px] w-full">
            <option value="active">Active</option>
            <option value="deprecating">Deprecating</option>
            <option value="retired">Retired</option>
            <option value="planned">Planned</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Stack components</label>
          <button type="button" onClick={addStackItem} className="btn-ghost text-[10px]">
            <Plus size={10} /> Add
          </button>
        </div>
        <div className="space-y-1">
          {stack.map((item, i) => (
            <div key={i} className="flex gap-1">
              <select value={item.type} onChange={e => updateStackItem(i, 'type', e.target.value)} title="Type"
                className="input py-1 text-[11px] w-24 shrink-0">
                <option value="runtime">runtime</option>
                <option value="framework">framework</option>
                <option value="database">database</option>
                <option value="infra">infra</option>
                <option value="os">os</option>
              </select>
              <input value={item.name} onChange={e => updateStackItem(i, 'name', e.target.value)}
                placeholder="name (nodejs, postgresql, …)"
                className="input py-1 text-[11px] flex-1" />
              <input value={item.version} onChange={e => updateStackItem(i, 'version', e.target.value)}
                placeholder="version"
                className="input py-1 text-[11px] w-24 shrink-0" />
              <button type="button" onClick={() => removeStackItem(i)} title="Remove"
                className="btn-ghost p-1 text-rose">
                <X size={11} />
              </button>
            </div>
          ))}
          {stack.length === 0 && (
            <div className="text-[10px] text-text-muted italic">No stack components yet — click "Add" to declare runtimes, frameworks, databases etc.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">URL</label>
          <input value={url || ''} onChange={e => setUrl(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="https://" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Repo URL</label>
          <input value={repoUrl || ''} onChange={e => setRepoUrl(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="https://github.com/..." />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Runbook URL</label>
          <input value={runbookUrl || ''} onChange={e => setRunbookUrl(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="Notion/Confluence link" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Tags</label>
          <input value={tags} onChange={e => setTags(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="pii, pci, customer-facing" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Dependencies (comma-separated service names)</label>
          <input value={dependencies} onChange={e => setDependencies(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="billing-db, auth-api" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button type="button"
          disabled={!name.trim()}
          onClick={() => onSave({
            name, description, tier, kind, owner, team, cloud, region, sla, status,
            url, repo_url: repoUrl, runbook_url: runbookUrl,
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            notes,
            stack: stack.filter(s => s.name.trim()),
            dependencies: dependencies.split(',').map(d => d.trim()).filter(Boolean),
          })}
          className="btn-primary text-xs">
          Save service
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  )
}

// ── Service detail drawer ────────────────────────────────────────────────────

function ServiceDetail({
  service, onClose, onEdit, onDelete,
}: {
  service: Service
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const tierMeta = TIER_META[service.tier]
  const KindIcon = KIND_ICON[service.kind] || Boxes
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-surface-card border-l border-border shadow-2xl z-40 overflow-y-auto">
      <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KindIcon size={14} className="text-text-muted" />
          <span className={clsx('text-[10px] font-semibold uppercase tracking-wider', tierMeta.color)}>
            {tierMeta.label}
          </span>
        </div>
        <button type="button" onClick={onClose} title="Close" className="text-text-muted hover:text-text-primary">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h2 className="text-base font-bold text-text-primary leading-snug">{service.name}</h2>
          {service.description && (
            <p className="text-[11px] text-text-secondary mt-1">{service.description}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Kind</div>
            <div className="text-text-primary capitalize">{service.kind}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Status</div>
            <div className={clsx('capitalize font-semibold', STATUS_COLOR[service.status] || 'text-text-primary')}>
              {service.status}
            </div>
          </div>
          {service.owner && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">Owner</div>
              <div className="text-text-primary">{service.owner}</div>
            </div>
          )}
          {service.team && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">Team</div>
              <div className="text-text-primary">{service.team}</div>
            </div>
          )}
          {service.cloud && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">Cloud</div>
              <div className="font-semibold" style={{ color: CLOUD_COLOR[service.cloud] || '#999' }}>
                {service.cloud.toUpperCase()} {service.region && <span className="text-text-muted font-normal">· {service.region}</span>}
              </div>
            </div>
          )}
          {service.sla && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">SLA</div>
              <div className="text-text-primary font-mono">{service.sla}</div>
            </div>
          )}
        </div>

        {service.stack.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <Layers size={10} /> Stack
            </div>
            <div className="flex flex-wrap gap-1">
              {service.stack.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-surface-raised text-text-secondary">
                  <span className="text-[9px] text-text-muted uppercase">{s.type}</span>
                  <span className="font-semibold text-text-primary">{s.name}</span>
                  {s.version && <span className="font-mono text-text-muted">{s.version}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {service.dependencies.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
              <Link2 size={10} /> Dependencies
            </div>
            <div className="flex flex-wrap gap-1">
              {service.dependencies.map(d => (
                <span key={d} className="px-1.5 py-0.5 rounded text-[10px] bg-blue/10 text-blue-light border border-blue/20 font-mono">
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {service.tags.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {service.tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-raised text-text-secondary">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {service.notes && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Notes</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap">{service.notes}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2">
          {service.url && (
            <a href={service.url} target="_blank" rel="noreferrer noopener" className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1">
              Live URL <ExternalLink size={10} />
            </a>
          )}
          {service.repo_url && (
            <a href={service.repo_url} target="_blank" rel="noreferrer noopener" className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1">
              Repo <ExternalLink size={10} />
            </a>
          )}
          {service.runbook_url && (
            <a href={service.runbook_url} target="_blank" rel="noreferrer noopener" className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1">
              Runbook <ExternalLink size={10} />
            </a>
          )}
        </div>

        <div className="pt-3 border-t border-border flex gap-2">
          <button type="button" onClick={onEdit} className="btn-secondary text-xs">
            <Edit2 size={11} /> Edit
          </button>
          <button type="button"
            onClick={() => { if (confirm(`Delete service "${service.name}"?`)) onDelete() }}
            className="btn-ghost text-xs text-rose">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ServiceCatalogPage() {
  const navigate = useNavigate()
  const [services, setServices] = useState<Service[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [riskByService, setRiskByService] = useState<Record<number, ServiceRisks>>({})
  const [health, setHealth] = useState<HealthSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Service | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Service | null>(null)

  const [searchQ, setSearchQ] = useState('')
  const [tierFilter, setTierFilter] = useState<number | null>(null)
  const [cloudFilter, setCloudFilter] = useState('')
  const [kindFilter, setKindFilter] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: list }, { data: s }, { data: r }, { data: h }] = await Promise.all([
        api.get('/api/services'),
        api.get('/api/services/stats'),
        api.get('/api/risks/by-service'),
        api.get('/api/services/health'),
      ])
      setServices(list.services)
      setStats(s)
      setRiskByService(r.services || {})
      setHealth(h)
    } finally { setLoading(false) }
  }, [])

  const healthById = useMemo(() => {
    const m: Record<number, ServiceHealth> = {}
    if (health) for (const s of health.services) m[s.id] = s
    return m
  }, [health])

  useEffect(() => { fetchData() }, [fetchData])

  const seedIfEmpty = async () => {
    if (services.length === 0) {
      await api.post('/api/services/seed')
      fetchData()
    }
  }

  const saveService = async (s: any) => {
    if (editing) {
      await api.put(`/api/services/${editing.id}`, s)
    } else {
      await api.post('/api/services', s)
    }
    setShowForm(false)
    setEditing(null)
    setSelected(null)
    fetchData()
  }

  const deleteService = async (id: number) => {
    await api.delete(`/api/services/${id}`)
    setSelected(null)
    fetchData()
  }

  const filtered = useMemo(() => {
    return services.filter(s => {
      if (tierFilter !== null && s.tier !== tierFilter) return false
      if (cloudFilter && s.cloud !== cloudFilter) return false
      if (kindFilter && s.kind !== kindFilter) return false
      if (searchQ) {
        const q = searchQ.toLowerCase()
        const hay = `${s.name} ${s.description} ${s.owner} ${s.team} ${s.tags.join(' ')}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [services, tierFilter, cloudFilter, kindFilter, searchQ])

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  const isEmpty = services.length === 0

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Boxes size={20} className="text-violet" /> Service Catalog
          </h1>
          <p className="text-[10px] text-text-secondary">
            {stats ? `${stats.total} services — tier 1: ${stats.by_tier[1] || 0} · tier 2: ${stats.by_tier[2] || 0} · tier 3: ${stats.by_tier[3] || 0}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEmpty && (
            <button type="button" onClick={seedIfEmpty} className="btn-secondary text-xs" title="Insert example services">
              <Plus size={12} /> Seed examples
            </button>
          )}
          <button type="button" onClick={fetchData} className="btn-ghost text-xs" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> New service
          </button>
        </div>
      </div>

      {/* Health summary */}
      {health && health.count > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Shield size={14} className="text-blue" />
              Stack Health
              <span className="text-[10px] text-text-muted font-normal">
                blends risks, hygiene, status, review freshness
              </span>
            </h2>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-text-muted">avg</span>
              <span className={clsx(
                'text-2xl font-bold font-mono',
                health.average >= 80 ? 'text-emerald'
                  : health.average >= 60 ? 'text-amber-light'
                  : health.average >= 40 ? 'text-orange' : 'text-rose'
              )}>{health.average}</span>
              <span className="text-text-muted">/ 100</span>
              <Sparkline
                metric="services_health_avg"
                hours={168}
                width={90}
                height={24}
                tone="up-good"
                showDelta
                deltaPercent
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {(['healthy', 'warning', 'concern', 'critical'] as HealthBand[]).map(b => (
              <div key={b} className={clsx('p-2.5 rounded border', HEALTH_BAND_BG[b])}>
                <div className="flex items-center justify-between">
                  <span className={clsx('text-[10px] uppercase tracking-wider font-semibold', HEALTH_BAND_TEXT[b])}>
                    {b}
                  </span>
                  <span className={clsx('text-lg font-bold', HEALTH_BAND_TEXT[b])}>
                    {health.by_band[b] || 0}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {health.worst.length > 0 && health.worst[0].score < 80 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">
                Lowest scoring
              </div>
              <div className="flex flex-wrap gap-1.5">
                {health.worst.filter(w => w.score < 80).map(w => (
                  <button
                    type="button"
                    key={w.id}
                    onClick={() => {
                      const svc = services.find(s => s.id === w.id)
                      if (svc) setSelected(svc)
                    }}
                    className={clsx(
                      'inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] hover:brightness-125 transition',
                      HEALTH_BAND_BG[w.band]
                    )}
                  >
                    <span className="font-bold font-mono">{w.score}</span>
                    <span className="font-semibold text-text-primary">{w.name}</span>
                    <span className="text-text-muted">T{w.tier}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Shield size={11} className="text-rose" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Tier 1 critical</span>
            </div>
            <div className="text-lg font-bold text-rose">{stats.by_tier[1] || 0}</div>
            <div className="text-[10px] text-text-muted">of {stats.total} total services</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Cloud size={11} className="text-blue" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Clouds in use</span>
            </div>
            <div className="text-lg font-bold text-blue">{Object.keys(stats.by_cloud).filter(k => k && k !== 'unspecified').length}</div>
            <div className="text-[10px] text-text-muted">
              {Object.entries(stats.by_cloud).filter(([k]) => k && k !== 'unspecified').slice(0, 3).map(([k, v]) => `${k} (${v})`).join(' · ')}
            </div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Layers size={11} className="text-emerald" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Unique runtimes</span>
            </div>
            <div className="text-lg font-bold text-emerald">
              {(stats.stack.runtime || []).length + (stats.stack.database || []).length}
            </div>
            <div className="text-[10px] text-text-muted">
              runtimes + databases across all services
            </div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Users size={11} className="text-amber" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Distinct owners</span>
            </div>
            <div className="text-lg font-bold text-amber-light">{stats.top_owners.length}</div>
            <div className="text-[10px] text-text-muted">
              top: {stats.top_owners[0]?.owner || '—'}
            </div>
          </div>
        </div>
      )}

      {/* Stack roll-up */}
      {stats && Object.keys(stats.stack).length > 0 && (
        <div className="card p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">
            Stack across all services
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['runtime', 'framework', 'database', 'infra', 'os'].filter(t => stats.stack[t]?.length).map(t => (
              <div key={t}>
                <div className="text-[9px] uppercase tracking-wider text-text-muted font-semibold mb-1">{t}</div>
                <div className="flex flex-wrap gap-1">
                  {stats.stack[t].map(item => (
                    <span key={item.name} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-raised">
                      <span className="text-text-primary font-semibold">{item.name}</span>
                      <span className="text-text-muted ml-1">×{item.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {services.length > 0 && (
        <div className="card p-3 flex flex-wrap items-center gap-3">
          <Filter size={12} className="text-text-muted" />
          <div className="flex gap-1">
            <button type="button" onClick={() => setTierFilter(null)}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                tierFilter === null ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>All tiers</button>
            {[1, 2, 3].map(n => (
              <button type="button" key={n} onClick={() => setTierFilter(n)}
                className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                  tierFilter === n ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>T{n}</button>
            ))}
          </div>
          <select value={cloudFilter} onChange={e => setCloudFilter(e.target.value)} title="Cloud filter"
            className="input py-1 text-[11px] pr-6">
            <option value="">All clouds</option>
            {stats && Object.entries(stats.by_cloud).filter(([k]) => k && k !== 'unspecified').map(([k]) => (
              <option key={k} value={k}>{k.toUpperCase()}</option>
            ))}
          </select>
          <select value={kindFilter} onChange={e => setKindFilter(e.target.value)} title="Kind filter"
            className="input py-1 text-[11px] pr-6">
            <option value="">All kinds</option>
            {stats && Object.keys(stats.by_kind).filter(k => k && k !== 'unspecified').map(k => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 ml-auto">
            <Search size={12} className="text-text-muted" />
            <input type="search" value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search name / owner / tag…"
              className="input py-1 text-[11px] w-48" />
          </div>
        </div>
      )}

      {/* Services list */}
      {isEmpty ? (
        <div className="card p-10 text-center">
          <Boxes size={36} className="text-text-muted mx-auto mb-3" />
          <div className="text-sm font-semibold text-text-primary mb-1">No services tracked yet</div>
          <p className="text-[11px] text-text-secondary mb-4 max-w-sm mx-auto">
            Declare the services you run. This powers ownership, tiering, and targeted risk
            auto-derivation (EOL / CVE warnings only for stack you actually use).
          </p>
          <button type="button" onClick={seedIfEmpty} className="btn-primary text-xs">
            <Plus size={12} /> Seed with example services
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-surface-raised/50">
                <tr className="border-b border-border">
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Service</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Tier</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Kind</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Owner</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Cloud</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Stack</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Status</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Risks</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Health</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-8" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const tierMeta = TIER_META[s.tier]
                  const KindIcon = KIND_ICON[s.kind] || Boxes
                  return (
                    <tr key={s.id}
                      onClick={() => setSelected(s)}
                      className="border-b border-border/50 hover:bg-surface-raised/50 transition-colors cursor-pointer">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <KindIcon size={11} className="text-text-muted" />
                          <span className="text-[11px] font-semibold text-text-primary">{s.name}</span>
                        </div>
                        {s.description && (
                          <div className="text-[10px] text-text-muted truncate max-w-md" title={s.description}>
                            {s.description}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded border font-semibold', tierMeta.bg, tierMeta.color)}>
                          T{s.tier}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[10px] text-text-secondary capitalize">{s.kind}</td>
                      <td className="py-2 px-3 text-[10px] text-text-secondary truncate max-w-[140px]">{s.owner || '—'}</td>
                      <td className="py-2 px-3 text-[10px]">
                        {s.cloud ? (
                          <span className="font-semibold" style={{ color: CLOUD_COLOR[s.cloud] || '#999' }}>
                            {s.cloud.toUpperCase()}
                          </span>
                        ) : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-0.5 max-w-xs">
                          {s.stack.slice(0, 4).map((si, i) => (
                            <span key={i} className="text-[9px] px-1 py-0.5 rounded bg-surface-raised text-text-secondary">
                              {si.name}{si.version && ` ${si.version}`}
                            </span>
                          ))}
                          {s.stack.length > 4 && (
                            <span className="text-[9px] text-text-muted">+{s.stack.length - 4}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-[10px]">
                        <span className={clsx('capitalize font-semibold', STATUS_COLOR[s.status] || 'text-text-primary')}>
                          {s.status}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {(() => {
                          const r = riskByService[s.id]
                          if (!r) return <span className="text-[10px] text-text-muted">—</span>
                          const color = r.top_priority >= 5 ? 'rose'
                            : r.top_priority >= 4 ? 'orange'
                            : r.top_priority >= 3 ? 'amber' : 'emerald'
                          return (
                            <button
                              type="button"
                              title={`${r.count} risk${r.count !== 1 ? 's' : ''} — top P${r.top_priority}`}
                              onClick={e => { e.stopPropagation(); navigate('/app/risk-matrix') }}
                              className={clsx(
                                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold',
                                color === 'rose' && 'bg-rose/10 border-rose/30 text-rose',
                                color === 'orange' && 'bg-orange/10 border-orange/30 text-orange',
                                color === 'amber' && 'bg-amber/10 border-amber/30 text-amber-light',
                                color === 'emerald' && 'bg-emerald/10 border-emerald/30 text-emerald'
                              )}
                            >
                              <ShieldAlert size={10} />
                              {r.count} · P{r.top_priority}
                            </button>
                          )
                        })()}
                      </td>
                      <td className="py-2 px-3">
                        {(() => {
                          const h = healthById[s.id]
                          if (!h) return <span className="text-[10px] text-text-muted">—</span>
                          const reasons = h.breakdown.length
                            ? h.breakdown.map(b => `${b.delta} ${b.reason}`).join('\n')
                            : 'Full marks'
                          return (
                            <div className="flex items-center gap-1.5" title={reasons}>
                              <div className="w-12 h-1.5 bg-surface-raised rounded-full overflow-hidden">
                                <div
                                  className={clsx('h-full',
                                    h.band === 'healthy' && 'bg-emerald',
                                    h.band === 'warning' && 'bg-amber',
                                    h.band === 'concern' && 'bg-orange',
                                    h.band === 'critical' && 'bg-rose',
                                  )}
                                  style={{ width: `${h.score}%` }}
                                />
                              </div>
                              <span className={clsx('text-[11px] font-mono font-bold w-8 text-right', HEALTH_BAND_TEXT[h.band])}>
                                {h.score}
                              </span>
                            </div>
                          )
                        })()}
                      </td>
                      <td className="py-2 px-3">
                        <ChevronRight size={12} className="text-text-muted" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-8 text-[11px] text-text-muted">
                No services match these filters.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <ServiceDetail
          service={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setShowForm(true); setSelected(null) }}
          onDelete={() => deleteService(selected.id)}
        />
      )}

      {/* Create / edit form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? `Edit service — ${editing.name}` : 'New service'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null) }} title="Close" className="text-text-muted hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <ServiceForm
                initial={editing || undefined}
                onSave={saveService}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
